import { useParams } from 'react-router-dom'
import { useFetchBlog } from './blogQueries'
import type { Blog } from './types'
import { H1 } from '@/common/ui/h1'
import { H2 } from '@/common/ui/h2'
import MoreBlogs from './MoreBlogs'
import { BLOGS_COPY } from '@/locales/en'
import { Alert } from '@/common/ui/alert'
import { Info } from 'lucide-react'

// lightweight skeleton that covers what's in the initial viewport
const ReadingSkeleton = () => (
    <div className="w-full max-w-[680px] py-6 inline-flex flex-col justify-start items-start gap-6 animate-pulse">
        <div className="self-stretch h-72 w-full relative bg-stone-100 rounded-2xl" />
        <div className="max-w-[520px]">
            <div className="h-8 bg-stone-100 w-64 rounded mt-4" />
        </div>
        <div className="inline-flex items-start gap-4 mt-3">
            <div className="w-16 h-16 bg-stone-100 rounded-full" />
            <div className="flex flex-col gap-2">
                <div className="h-3 w-36 bg-stone-100 rounded" />
                <div className="h-3 w-24 bg-stone-100 rounded" />
            </div>
        </div>
        <div className="h-4 w-full bg-stone-100 rounded mt-6" />
        <div className="h-4 w-5/6 bg-stone-100 rounded mt-2" />
    </div>
)

export function BlogPage(): JSX.Element {
    const { slug } = useParams()
    const { data: raw, isLoading } = useFetchBlog(slug ?? null, !!slug)
    const blog = raw as Blog | undefined

    return (
        <div className="w-full flex justify-start">
            {isLoading ? (
                <ReadingSkeleton />
            ) : blog ? (
                <div className="w-full max-w-[680px] py-6 inline-flex flex-col justify-start items-start gap-12">
                    <div
                        className="self-stretch h-72 relative bg-brand-dust rounded-2xl overflow-hidden"
                        style={{
                            backgroundImage: blog?.thumbnailImage?.url ? `url("${blog.thumbnailImage.url}")` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    >
                        {blog?.thumbnailImage?.url && (
                            <img
                                src={blog.thumbnailImage.url}
                                alt={blog?.thumbnailImage?.alt ?? blog.title}
                                className="sr-only"
                            />
                        )}
                    </div>

                    <div className="self-stretch flex flex-col justify-start items-start gap-6">
                        {blog?.category && (
                            <div className="px-3 py-1.5 bg-primary-dust rounded-2xl backdrop-blur-[5px] inline-flex justify-start items-center gap-1 overflow-hidden">
                                <div className="justify-center text-black text-xs font-normal font-['Inter'] leading-4">
                                    {blog.category}
                                </div>
                            </div>
                        )}

                        <div className="self-stretch flex flex-col justify-start items-start gap-12">
                            <div className="self-stretch flex flex-col justify-start items-start gap-6">
                                <div className="max-w-[520px] flex flex-col justify-start items-start gap-4">
                                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                                        <div className="self-stretch inline-flex justify-start items-center gap-2">
                                            <H1 className="justify-center text-black text-4xl font-normal font-['Tangerine'] leading-10 mb-0">
                                                {blog.title}
                                            </H1>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col justify-start items-start gap-4">
                            <div className="inline-flex justify-start items-center gap-4">
                                <div className="inline-flex flex-row justify-start items-start gap-4">
                                    <div
                                        className="w-16 h-16 relative rounded-full overflow-hidden bg-stone-100"
                                        style={
                                            (blog?.author?.thumbnailImage?.url
                                                ? {
                                                      backgroundImage: `url(${blog.author.thumbnailImage.url})`,
                                                      backgroundSize: 'cover',
                                                      backgroundPosition: 'center',
                                                  }
                                                : undefined) as React.CSSProperties
                                        }
                                    >
                                        {blog?.author?.thumbnailImage?.url && (
                                            <img
                                                src={blog.author.thumbnailImage.url}
                                                alt={blog?.author?.name ?? 'Author avatar'}
                                                className="sr-only"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col justify-start items-start gap-1">
                                        <div className="justify-center text-black text-base font-medium font-['Inter'] leading-6">
                                            {blog.author?.name}
                                        </div>
                                        <div className="justify-center text-black text-sm font-normal font-['Inter'] leading-5">
                                            {blog.author?.role}
                                        </div>
                                        <div className="inline-flex justify-start items-center gap-2">
                                            <div className="justify-center text-content-tertiary text-sm font-normal font-['Inter'] leading-5">
                                                {blog.publishedDate &&
                                                    new Date(blog.publishedDate).toLocaleDateString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    })}
                                            </div>
                                            {blog.duration && (
                                                <>
                                                    <div className="w-1 h-1 relative bg-content-tertiary rounded-[100px]" />
                                                    <div className="justify-center text-content-tertiary text-sm font-normal font-['Inter'] leading-5">
                                                        {blog.duration} {BLOGS_COPY.read}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {blog?.content &&
                        blog.content.map((block, i) => (
                            <div key={i} className="flex flex-col justify-start items-start gap-4">
                                {block.header && (
                                    <H2 className="justify-center text-black text-3xl font-normal font-['Serious_Sans'] leading-10">
                                        {block.header}
                                    </H2>
                                )}
                                {block.body && (
                                    <div
                                        className="justify-center text-black text-lg font-normal font-['Inter'] leading-6 inserted-html"
                                        dangerouslySetInnerHTML={{ __html: block.body }}
                                    />
                                )}
                            </div>
                        ))}

                    {blog?.disclaimer && (
                        <Alert variant="default">
                            <div className="self-stretch inline-flex justify-start items-start gap-4">
                                <Info size={20} className="relative text-content-secondary" />
                                <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                                    <div
                                        className="justify-center text-sm font-normal font-['Inter'] text-content-secondary leading-5"
                                        dangerouslySetInnerHTML={{ __html: blog.disclaimer }}
                                    />
                                </div>
                            </div>
                        </Alert>
                    )}

                    <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider" />

                    <MoreBlogs title={BLOGS_COPY.readMore} current={blog} />
                </div>
            ) : (
                <div className="w-full flex flex-col">
                    <div className="w-full max-w-[680px] py-6">{BLOGS_COPY.notFoundTitle}</div>
                    <MoreBlogs title={BLOGS_COPY.readMore} />
                </div>
            )}
        </div>
    )
}
